import { useState, useMemo } from "react";
import { Stack, TextField, Button } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import { NearbyWorkerSearchResult } from "../Autocomplete/useGetNearbyWorkers";
import { logEvent } from "@src/appV2/lib/analytics";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { useWorkplaceIds } from "@src/appV2/api/useWorkplaceIds";

interface HighCallOffConfirmationProps {
  selectedWorker: NearbyWorkerSearchResult;
  totalCallOffs?: number;
  onInviteSomeoneElse: () => void;
  onProceed: (confirmed: boolean) => void;
}

export function HighCallOffConfirmation({
  selectedWorker,
  totalCallOffs,
  onInviteSomeoneElse,
  onProceed,
}: HighCallOffConfirmationProps) {
  const [confirmationText, setConfirmationText] = useState("");
  const { workplaceId, workplaceUserId } = useWorkplaceIds();

  // Log event when high call-off confirmation is requested
  useMemo(() => {
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
      action: "HIGH_CALL_OFF_REQUESTED_CONFIRMATION",
      workerId: selectedWorker?.workerId,
      workerName: selectedWorker?.fullName,
      totalCallOffs,
      workplaceId,
      workplaceUserId,
    });
  }, [
    selectedWorker?.workerId,
    selectedWorker?.fullName,
    totalCallOffs,
    workplaceId,
    workplaceUserId,
  ]);

  // Check if user confirmation is valid
  const isConfirmationValid = confirmationText.trim().toLowerCase() === "i understand";

  return (
    <Stack spacing={2} sx={{ mt: 2 }}>
      <Text variant="body2">
        Please type "I understand" to proceed with inviting this professional.
      </Text>
      <TextField
        fullWidth
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        placeholder="Type 'I understand' here"
        size="small"
      />
      <Stack spacing={1} direction="row" justifyContent="flex-end">
        <Button onClick={onInviteSomeoneElse} variant="outlined">
          Invite Someone Else
        </Button>
        <Button
          onClick={() => {
            if (isConfirmationValid) {
              logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
                action: "HIGH_CALL_OFF_CONFIRMATION_ACCEPTED",
                workerId: selectedWorker?.workerId,
                workerName: selectedWorker?.fullName,
                totalCallOffs,
                workplaceId,
                workplaceUserId,
              });
            }
            onProceed(isConfirmationValid);
          }}
          variant="contained"
          endIcon={<ArrowForwardIosOutlinedIcon />}
          disabled={!isConfirmationValid}
        >
          Next
        </Button>
      </Stack>
    </Stack>
  );
}
